/**
 * Where the two tokens live, and why they live in different places.
 *
 * ACCESS TOKEN — memory only. It is a 15-minute bearer credential; putting it
 * in localStorage would leave it readable by any injected script for its whole
 * life, and it buys nothing, because a page reload can mint a fresh one from
 * the refresh token in a single round trip.
 *
 * REFRESH TOKEN — localStorage. It has to survive a reload, and this API is
 * Bearer-only with no cookies (CLAUDE.md § Auth invariants), so there is no
 * httpOnly option available to us. The mitigations are server-side and already
 * built: rotation on every use, reuse detection that kills the session, and the
 * Redis denylist.
 *
 * Nothing here decides WHEN to clear tokens. That decision belongs to
 * `isCredentialVerdict()` in @seagames/contracts and lives in one place.
 */

const REFRESH_KEY = "seagames.refresh_token";

/** Never persisted. Lost on reload, by design. */
let accessToken: string | null = null;

/**
 * localStorage throws in some privacy modes and inside sandboxed frames. A
 * storage failure must degrade to "you have to sign in again after a reload",
 * never to a white screen.
 */
function safeRead(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

function safeWrite(key: string, value: string | null): void {
	try {
		if (value === null) localStorage.removeItem(key);
		else localStorage.setItem(key, value);
	} catch {
		/* memory-only session; acceptable degradation */
	}
}

export const getAccessToken = (): string | null => accessToken;
export const getRefreshToken = (): string | null => safeRead(REFRESH_KEY);

/**
 * True when a session might exist. Used by the router guard to decide whether
 * to attempt a silent restore or go straight to /login — it cannot be certain,
 * because only the server can rule on the token.
 */
export const hasStoredSession = (): boolean => getRefreshToken() !== null;

export function setTokens(
	next: { accessToken: string; refreshToken?: string | undefined },
	/**
	 * Compare-and-swap: only persist the rotation if the token we rotated FROM
	 * is still the stored one.
	 *
	 * localStorage is shared by every tab on this origin, but each tab runs its
	 * own copy of this module. Without the check, a slow refresh — a tab that
	 * was frozen while the laptop slept, then resumed — can land after a newer
	 * rotation and write its already-superseded token back over the good one.
	 * The next refresh then presents a dead token, which is indistinguishable
	 * from a stolen one, and the API kills the session (`REFRESH_REUSE`). That
	 * is the forced-logout this guard exists to prevent; the cross-tab lock in
	 * `api/client.ts` is the other half.
	 *
	 * Dropping the write is safe: whoever moved the value on holds a token that
	 * is newer than ours, and the access token we just received is still valid
	 * for its full life.
	 */
	expect?: { refreshToken: string },
): void {
	accessToken = next.accessToken;
	// The refresh endpoint omits `refresh_token` in the grace-window case: the
	// caller keeps the one it already has. Overwriting with undefined there
	// would sign the user out on a StrictMode double-mount.
	if (next.refreshToken === undefined) return;
	if (expect && safeRead(REFRESH_KEY) !== expect.refreshToken) return;
	safeWrite(REFRESH_KEY, next.refreshToken);
}

type SignOutReason = "revoked" | "expired" | "user";
type Listener = (reason: SignOutReason) => void;
const listeners = new Set<Listener>();

/** Fired only when the SERVER has ruled on the credential, or the user asked. */
export function onSignOut(fn: Listener): () => void {
	listeners.add(fn);
	return () => listeners.delete(fn);
}

export function clearTokens(reason: SignOutReason = "user"): void {
	accessToken = null;
	safeWrite(REFRESH_KEY, null);
	for (const fn of listeners) fn(reason);
}
